import { MouseEventHandler, ResponsivePie } from "@nivo/pie";
import { animated } from "@react-spring/web";
import { useContext, useEffect, useMemo, useState } from "react";
import { calculateGraphTwoDigitPercentage, pSBC, truncateLongText } from "utilities/utilities";
import { theme, useStyles } from "./DowntimeSummaryByFailureType.styles";
import { CircularProgress, useTheme } from "@mui/material";
import { ComputedDatum } from "@nivo/bar";
import { ISelectedFailure } from "contexts/CorporateDashboardContext/CorporateDashboardContextInterfaces";
import MainSmallTable from "components/MainSmallTable";
import { useTranslation } from "react-i18next";
import { IMainSmallTableData } from "components/MainSmallTable/MainSmallTableInterfaces";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import { IPieChartData } from "./DowntimeSummaryByFailureTypeInterfaces";
import NoData from "components/NoData/NoData.view";
import { DisplayInOptionsEnum } from "constants/constants";
import { IMillReportCardProps } from "../../../MillDashboardInterfaces";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";

const DowntimeSummaryByFailureType = ({ countryCode }: IMillReportCardProps) => {
    const classes = useStyles();
    const themeStyles = useTheme();
    const { millId } = useParams();
    const [selectedPortion, setSelectedPortion] = useState<number | null>(null);
    const { handleSetSelectedFailureType, selectedFailureType, filters, handleSetTotalDowntimeSeconds } =
        useContext(SummaryReportDashboardContext);

    const {
        displayUnit: displayIn,
        reportRangeEndDate: endDate,
        reportRangeStartDate: startDate,
    } = useContext(MillDashboardContext);

    const { t } = useTranslation();
    const { downtimeSummaryByFailureTypeTotal, loading } =
        MillsEntity.GetDowntimeSummaryTotalsByFailureTypeMillDashboard(
            { ...filters, startDate, endDate, displayIn },
            +millId!,
        );

    useEffect(() => {
        const totalDowntimeSeconds = downtimeSummaryByFailureTypeTotal.reduce(
            (acc, current) => acc + current.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.seconds,
            0,
        );
        handleSetTotalDowntimeSeconds(totalDowntimeSeconds);
    }, [downtimeSummaryByFailureTypeTotal, handleSetTotalDowntimeSeconds]);

    const pieChartDataMemoized: IPieChartData[] = useMemo(() => {
        const pieChartData: IPieChartData[] = downtimeSummaryByFailureTypeTotal.map((downtime) => {
            return {
                id: downtime.failure_type_id,
                name: truncateLongText(downtime.name, 20),
                value:
                    displayIn === DisplayInOptionsEnum.OCCURRENCES
                        ? downtime.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.occurrences
                        : Math.round(downtime.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.seconds / 60) <
                          1
                        ? 1
                        : Math.round(downtime.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.seconds / 60),
                color_code: downtime.color_code,
                default_failure_type_translations: downtime.default_failure_type_translations,
            };
        });

        return pieChartData;
    }, [downtimeSummaryByFailureTypeTotal, displayIn]);

    const aggregateTotalValue = useMemo(() => {
        const totalSeconds = downtimeSummaryByFailureTypeTotal.reduce(
            (acc, current) => acc + current.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.seconds,
            0,
        );
        const totalOccurrences = downtimeSummaryByFailureTypeTotal.reduce(
            (acc, current) => acc + current.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.occurrences,
            0,
        );
        const totalMinutes = Math.round(totalSeconds / 60);

        if (displayIn === DisplayInOptionsEnum.OCCURRENCES) {
            return totalOccurrences;
        } else {
            return totalMinutes < 1 ? 1 : totalMinutes;
        }
    }, [downtimeSummaryByFailureTypeTotal, displayIn]);

    const fills = pieChartDataMemoized.map((dataItem) => ({
        match: {
            id: dataItem.id,
        },
        id: dataItem.name,
    }));

    const handlePieClick = (datum: ComputedDatum<IPieChartData>) => {
        const parsedSelectedFailureType = {
            id: datum.id,
            name: datum.data.name,
            color_code: datum.data.color_code,
            default_failure_type_translations: datum.data.default_failure_type_translations,
        };
        handleSetSelectedFailureType(parsedSelectedFailureType as ISelectedFailure);
        setSelectedPortion(Number(datum.id));
    };

    const colorMapping: Record<string, string> = pieChartDataMemoized.reduce((colors: Record<string, string>, item) => {
        if (!selectedPortion) {
            colors[item.id] = item.color_code;
            return colors;
        } else if (selectedPortion === item.id) {
            colors[item.id] = item.color_code;
            return colors;
        } else {
            colors[item.id] = pSBC(0.4, item.color_code) || item.color_code;
            return colors;
        }
    }, {});

    return loading ? (
        <div className={classes.noData}>
            <CircularProgress size={24} color="info" />
        </div>
    ) : pieChartDataMemoized.length > 0 ? (
        <div className={classes.wrapper}>
            <div className={classes.side}>
                <ResponsivePie
                    colors={(e) => colorMapping[e.id]}
                    data={pieChartDataMemoized}
                    theme={theme}
                    margin={{ top: 0, right: 10, bottom: 0, left: 10 }}
                    innerRadius={0}
                    padAngle={0}
                    cornerRadius={0}
                    activeOuterRadiusOffset={8}
                    arcLabelsRadiusOffset={0.6}
                    borderWidth={1}
                    borderColor={{
                        from: "color",
                        modifiers: [["darker", 0.2]],
                    }}
                    enableArcLabels={true}
                    enableArcLinkLabels={false}
                    arcLinkLabelsSkipAngle={2}
                    arcLinkLabelsTextColor={themeStyles.palette.text.primary}
                    arcLinkLabelsThickness={1}
                    arcLinkLabelsColor={themeStyles.palette.text.primary}
                    arcLabelsSkipAngle={10}
                    arcLabelsTextColor={{
                        from: "color",
                        modifiers: [["darker", 3]],
                    }}
                    layers={["arcs", "arcLabels", "arcLinkLabels", "legends"]}
                    fill={fills}
                    fit
                    legends={[]}
                    onClick={handlePieClick as unknown as MouseEventHandler<IPieChartData, SVGPathElement>}
                    arcLabelsComponent={({ datum, style }) => {
                        const calculatedPercentage = calculateGraphTwoDigitPercentage(datum.value, aggregateTotalValue);
                        return (
                            <animated.g className={classes.arcLabelWrapper} transform={style.transform}>
                                <text className={classes.arcLabel} fill={style.textColor}>
                                    {calculatedPercentage ? `${calculatedPercentage}%` : ""}
                                </text>
                            </animated.g>
                        );
                    }}
                />
            </div>
            <div className={classes.rightSection}>
                <MainSmallTable
                    data={pieChartDataMemoized}
                    total={aggregateTotalValue}
                    countryCode={countryCode}
                    labelTitle={t(
                        "corporate_dashboard.downtime_summary_by_type.downtime_summary_by_type_table.downtime_type",
                    )}
                    valueTitle={t(
                        displayIn === DisplayInOptionsEnum.OCCURRENCES
                            ? "corporate_dashboard.downtime_summary_by_type.downtime_summary_by_type_table.occurrences"
                            : "corporate_dashboard.downtime_summary_by_type.downtime_summary_by_type_table.minutes",
                    )}
                    selectedRow={selectedFailureType as unknown as IMainSmallTableData}
                    onClickRow={(row) => {
                        handleSetSelectedFailureType(row as unknown as ISelectedFailure);
                        setSelectedPortion(Number(row.id));
                    }}
                />
            </div>
        </div>
    ) : (
        <NoData className={classes.noData} />
    );
};

export default DowntimeSummaryByFailureType;
